import React, { useCallback, useState } from "react";

import { currentReturnPath, startSheetsSignIn } from "../lib/sheets-oauth";
import type { SheetsReconnectInfo } from "../lib/sheets-reconnect";
import { useAuth } from "../lib/useAuth";
import { Button } from "./ui/Button";

type SheetsReconnectBannerProps = {
  info: SheetsReconnectInfo;
  className?: string;
};

export const SheetsReconnectBanner: React.FC<SheetsReconnectBannerProps> = ({
  info,
  className = "",
}) => {
  const token = useAuth((state) => state.token);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const onClick = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await startSheetsSignIn(token, currentReturnPath());
      if (result.status === "ready") {
        window.location.assign(result.url);
        return;
      }
      setMessage(result.message);
    } catch {
      setMessage("Could not start Google sign-in. Try again.");
    } finally {
      setBusy(false);
    }
  }, [token]);

  return (
    <div
      role="alert"
      data-testid="sheets-reconnect-banner"
      data-reconnect-kind={info.kind}
      className={`flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center md:justify-between ${className}`}
      style={{
        borderColor: "color-mix(in srgb, var(--od-warning) 45%, transparent)",
        backgroundColor: "color-mix(in srgb, var(--od-warning) 10%, transparent)",
      }}
    >
      <div className="min-w-0">
        <p className="m-0 text-sm font-semibold text-od-text">{info.title}</p>
        <p className="m-0 mt-0.5 text-xs text-od-muted">{info.body}</p>
        {message && <p className="m-0 mt-0.5 text-xs text-od-danger">{message}</p>}
      </div>
      <Button variant="primary" size="sm" loading={busy} onClick={onClick}>
        {info.action}
      </Button>
    </div>
  );
};

export default SheetsReconnectBanner;
